import React from 'react';
import { Column, ColumnHeading, ColumnList, PanelInner, TileGrid } from './CategoryMegaNav.styles';
import { MegaNavLink } from './MegaNavLink';
import type { ResolvedMegaNavColumn, ResolvedMegaNavLink, ResolvedMegaNavSlot } from './useResolvedMegaNav';

/** `variant` separates a picture tile from the text row for the same facet — both exist in one panel. */
export type MegaNavLinkClickHandler = (
  link: ResolvedMegaNavLink,
  context: Readonly<{ slot: ResolvedMegaNavSlot; column?: ResolvedMegaNavColumn; variant: 'tile' | 'text' }>,
) => void;

type MegaNavPanelProps = Readonly<{
  slot: ResolvedMegaNavSlot;
  onLinkClick: MegaNavLinkClickHandler;
}>;

export const MegaNavPanel = ({ slot, onLinkClick }: MegaNavPanelProps) => {
  return (
    <PanelInner
      role="group"
      aria-label={slot.label}
      data-testid={`category-mega-nav-panel-${slot.id}`}
    >
      {slot.tiles && slot.tiles.length > 0 && (
        <TileGrid
          $columns={slot.tiles.length === 3 ? 3 : 2}
          data-testid="category-mega-nav-tiles"
        >
          {slot.tiles.map(tile => (
            <MegaNavLink
              key={tile.id}
              link={tile}
              variant="tile"
              onClick={() => onLinkClick(tile, { slot, variant: 'tile' })}
            >
              <img
                src={tile.imageSrc}
                alt=""
                loading="lazy"
              />
              <span>{tile.label}</span>
            </MegaNavLink>
          ))}
        </TileGrid>
      )}
      {slot.columns.map(column => (
        <Column
          key={column.id}
          data-testid={`category-mega-nav-column-${column.id}`}
        >
          <ColumnHeading>{column.heading}</ColumnHeading>
          <ColumnList>
            {column.links.map(link => (
              <li key={link.id}>
                <MegaNavLink
                  link={link}
                  onClick={() => onLinkClick(link, { slot, column, variant: 'text' })}
                >
                  {link.label}
                </MegaNavLink>
              </li>
            ))}
          </ColumnList>
        </Column>
      ))}
    </PanelInner>
  );
};
